/* eslint-disable react/no-multi-comp */
/* eslint-disable relay/unused-fields */
import { NewsletterVariant, theme, useResponsive } from "@product/scmp-sdk";
import type { NewsletterItem } from "@product/scmp-sdk/dist/types/components/newsletter/types";
import type { FunctionComponent } from "react";
import { Fragment } from "react";
import { graphql, useFragment, usePaginationFragment } from "react-relay";

import { AdSlot } from "~/components/advertisement/ad-slots/ad-slot";
import { TargetingPaidType } from "~/components/advertisement/ad-slots/types";
import { useNewsletterWidget } from "~/components/newsletter/newsletter-widget-container/hooks";
import { checkIsSuperTopic } from "~/components/topic";
import { data as appData, newsletter } from "~/data";
import { useInfiniteScrollTriggerReference } from "~/lib/hooks";
import type { topicContentListTopic$key } from "~/queries/__generated__/topicContentListTopic.graphql";
import type { topicContentListTopicSuperTopicQueueQuery$key } from "~/queries/__generated__/topicContentListTopicSuperTopicQueueQuery.graphql";
import type { topicSponsorArticlesQueueQuery$key } from "~/queries/__generated__/topicSponsorArticlesQueueQuery.graphql";

import {
  ContentContainer,
  Item,
  MainContainer,
  NativeAdContainer,
  NewsletterAdsContainer,
  NewsletterAndAdsContainer,
  StyledAdSlot,
  StyledLoading,
  StyledNewsletterWidgetContainer,
  StyledTopicSponsorArticles,
} from "./styles";

type Props = {
  className?: string;
  showColumnAndOpinionArticles: boolean;
  sponsorArticleReference: topicSponsorArticlesQueueQuery$key;
  superTopicReference: topicContentListTopicSuperTopicQueueQuery$key;
  topicReference: topicContentListTopic$key;
};

const topicPageSize = 30;

export const TopicContentListTopic: FunctionComponent<Props> = ({
  className,
  showColumnAndOpinionArticles,
  sponsorArticleReference: sponsorArticleReference_,
  superTopicReference: superTopicReference_,
  topicReference: topicReference_,
}) => {
  const {
    data,
    hasNext: hasNextPage,
    isLoadingNext: isLoading,
    loadNext,
  } = usePaginationFragment(
    graphql`
      fragment topicContentListTopic on Topic
      @argumentDefinitions(
        after: { type: "String" }
        count: { type: "Int", defaultValue: 30 }
        applicationIds: { type: "[String]", defaultValue: [] }
      )
      @refetchable(queryName: "topicContentListPaginationQuery") {
        contents(
          after: $after
          first: $count
          filter: { applicationIds: $applicationIds }
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
        ) @connection(key: "topicContentList__topic_contents") {
          edges {
            node {
              __typename
              entityId
              ...contentItemRenderContent
              ... on Article {
                types {
                  value {
                    entityUuid
                  }
                }
              }
            }
          }
        }
        relatedNewsletters {
          entityId
          name
          alternativeName
          summary {
            text
          }
          homepageDescription {
            text
          }
        }
        entityId
        advertZone
      }
    `,
    topicReference_,
  );

  const superTopicData = useFragment(
    graphql`
      fragment topicContentListTopicSuperTopicQueueQuery on Query
      @argumentDefinitions(entityId: { type: "String" }, superTopicQueueName: { type: "String" }) {
        topic(filter: { entityId: $entityId }) {
          ...topicCheckSuperTopic
          ...contentTopicLinkSuperTopic
        }
        superTopicQueue: queue(filter: { name: $superTopicQueueName }) {
          items(first: 10) {
            edges {
              node {
                __typename
                ...contentItemRenderContent
                ... on Content {
                  entityId
                }
                ... on Article {
                  types {
                    value {
                      entityUuid
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
    superTopicReference_,
  );

  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"), true);

  type QueueEdge = Exclude<
    Exclude<(typeof superTopicData)["superTopicQueue"], null>["items"],
    null
  >["edges"][number];

  const isSuperTopic = checkIsSuperTopic(superTopicData.topic);
  const superTopicEdges = isSuperTopic ? superTopicData.superTopicQueue?.items?.edges ?? [] : [];
  const excludeEntityIds = new Set(superTopicEdges.map((edge: QueueEdge) => edge.node.entityId));

  const topicEdges = data.contents?.edges ?? [];
  /*
  NOTE: Reason for handling the filter of articles here and not part of the fragment is because
  we need to know what type the topic is so that we can show the correct types of articles
  (in the case of topics with type opinion) and this can only be done after we have fetch the
  data and not before
  */
  const combinedEdges = [...superTopicEdges, ...topicEdges].filter(data => {
    if (excludeEntityIds.has(data.node.entityId)) {
      return false;
    }
    const flattenedTypes = (data.node?.types ?? []).flatMap(type =>
      (type?.value ?? []).map(v => v?.entityUuid ?? ""),
    );

    const isColumnOrOpinionType =
      flattenedTypes?.includes(appData.articleTypeEntityUuids.Column) ||
      flattenedTypes?.includes(appData.articleTypeEntityUuids.Opinion);

    return showColumnAndOpinionArticles ? isColumnOrOpinionType : !isColumnOrOpinionType;
  });

  const { dataMatrix } = useNewsletterWidget({
    contentType: "topic",
    eventCategory: "Newsletter (Topic - Inline)",
    section: "",
  });

  const { infiniteScrollTriggerReference } = useInfiniteScrollTriggerReference({
    hasNextPage,
    isLoading,
    onLoadMore: () => loadNext(topicPageSize),
  });

  return (
    <MainContainer className={className}>
      <ContentContainer>
        {combinedEdges.map(({ node }, index) => (
          <Fragment key={node.entityId}>
            <Item
              reference={node}
              variant={index === 0 ? "primary" : "secondary"}
              withTopicLink={{
                variant: { superTopicReference: superTopicData.topic, type: "sub-topic" },
              }}
              withAuthorLink
            />
            {index === Math.min(combinedEdges.length - 1, 2) && (
              <NewsletterAndAdsContainer>
                <StyledNewsletterWidgetContainer
                  dataMatrix={dataMatrix}
                  newsletterId={newsletter.globalImpact.entityId}
                  newsletterItems={(data?.relatedNewsletters ?? []) as NewsletterItem[]}
                  queryParametersForSocialButtons={{}}
                  variant={NewsletterVariant.Topic}
                />
                <NewsletterAdsContainer>
                  <StyledAdSlot
                    adUnit="d_lrec1"
                    sizes={[[300, 250], "fluid"]}
                    targeting={{
                      paid: TargetingPaidType.Free,
                      sctid: data.entityId,
                    }}
                    withBackground={!isDesktopUp}
                    withLabel={!isDesktopUp}
                    zone={data.advertZone}
                  />
                </NewsletterAdsContainer>

                <NativeAdContainer>
                  <AdSlot
                    adUnit={isDesktopUp ? "d_native2c" : "m_native2c"}
                    fluidDefaultSize={isDesktopUp ? { height: 163 } : { height: 132 }}
                    sizes={["fluid"]}
                    targeting={{
                      paid: TargetingPaidType.Free,
                      sctid: data.entityId,
                    }}
                    zone={data.advertZone}
                  />
                </NativeAdContainer>
              </NewsletterAndAdsContainer>
            )}
            {index === 7 && isSuperTopic && (
              <StyledTopicSponsorArticles reference={sponsorArticleReference_} />
            )}
          </Fragment>
        ))}
      </ContentContainer>
      {hasNextPage && <StyledLoading ref={element => infiniteScrollTriggerReference(element)} />}
    </MainContainer>
  );
};

TopicContentListTopic.displayName = "TopicContentListTopic";
